import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, List, ListOrdered, Heading2, Heading3,
  Link as LinkIcon, Image as ImageIcon, Undo, Redo, Quote,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const Btn = ({ active, onClick, title, children, testid }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`w-9 h-9 flex items-center justify-center border transition-colors ${
      active ? "bg-ink text-bone border-ink" : "border-ink/15 text-ink hover:border-claret hover:text-claret"
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer" } }),
      Image.configure({ HTMLAttributes: { class: "blog-body-img" } }),
      Placeholder.configure({ placeholder: placeholder || "Scrivi il tuo articolo…" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose-blog outline-none min-h-[280px] px-4 py-3",
        "data-testid": "post-content-editor",
      },
    },
  });

  // Sync esterno (es. caricamento articolo in modifica) senza rompere il cursore.
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL del link:", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkReselection?.().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const onPickImage = () => fileRef.current?.click();
  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      toast.loading("Caricamento immagine…", { id: "body-img" });
      const { data } = await api.post("/blog/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Salva l'URL relativo ('/api/images/..') per portabilità tra preview e produzione.
      editor.chain().focus().setImage({ src: data.url }).run();
      toast.success("Immagine inserita", { id: "body-img" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fallito", { id: "body-img" });
    }
  };

  return (
    <div className="border border-ink/20">
      <div className="flex flex-wrap gap-1 p-2 border-b border-ink/15 bg-linen/40">
        <Btn testid="rte-bold" title="Grassetto" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></Btn>
        <Btn testid="rte-italic" title="Corsivo" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></Btn>
        <span className="w-px bg-ink/10 mx-1" />
        <Btn testid="rte-h2" title="Titolo" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></Btn>
        <Btn testid="rte-h3" title="Sottotitolo" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={16} /></Btn>
        <span className="w-px bg-ink/10 mx-1" />
        <Btn testid="rte-ul" title="Elenco puntato" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></Btn>
        <Btn testid="rte-ol" title="Elenco numerato" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></Btn>
        <Btn testid="rte-quote" title="Citazione" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={16} /></Btn>
        <span className="w-px bg-ink/10 mx-1" />
        <Btn testid="rte-link" title="Link" active={editor.isActive("link")} onClick={setLink}><LinkIcon size={16} /></Btn>
        <Btn testid="rte-image" title="Inserisci immagine" onClick={onPickImage}><ImageIcon size={16} /></Btn>
        <span className="w-px bg-ink/10 mx-1" />
        <Btn testid="rte-undo" title="Annulla" onClick={() => editor.chain().focus().undo().run()}><Undo size={16} /></Btn>
        <Btn testid="rte-redo" title="Ripeti" onClick={() => editor.chain().focus().redo().run()}><Redo size={16} /></Btn>
        <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="rte-image-input" />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
